// https://css-tricks.com/building-progress-ring-quickly/
export function RingProgressBar({
	strokeColor,
	color,
	radius,
	stroke,
	progress,
	text,
	className,
	inset = true,
}: {
	strokeColor: string;
	color: string;
	radius: number;
	stroke: number;
	progress: number;
	text?: string | number;
	/**
	 * Overrides the rendered size. The `viewBox` scales the drawing to match. Also where the
	 * caller supplies the negative margin that claws back the padding left by `inset`: that
	 * padding is a fraction of the rendered size, so it has to track whatever size is set here
	 * rather than being a fixed pixel value baked into this component.
	 */
	className?: string;
	/**
	 * Keeps the ring's internal padding (~11% of the box on each side) so a caller can pull
	 * neighbours into it with a negative margin. Set `false` when the ring is laid out as a
	 * plain icon and must keep its own box: the drawing is then cropped to the stroke so it
	 * fills the box edge to edge.
	 */
	inset?: boolean;
}) {
	const normalizedRadius = radius - stroke * 2;
	const circumference = normalizedRadius * 2 * Math.PI;
	const strokeDashoffset = circumference - (progress / 100) * circumference;

	// Outer edge of the painted stroke, measured from the centre.
	const painted = normalizedRadius + stroke / 2;
	const viewBox = inset
		? `0 0 ${radius * 2} ${radius * 2}`
		: `${radius - painted} ${radius - painted} ${painted * 2} ${painted * 2}`;

	return (
		<svg height={radius * 2} width={radius * 2} viewBox={viewBox} className={className}>
			{/* Placeholder */}
			<circle
				stroke={strokeColor}
				fill="transparent"
				className="opacity-35"
				strokeWidth={stroke}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			{/* Bar */}
			<circle
				className="-rotate-90 transition-[stroke-dashoffset] duration-600"
				stroke={color}
				fill="transparent"
				strokeWidth={stroke}
				strokeDasharray={`${circumference} ${circumference}`}
				// The pivot must be the circle's own centre in user units. `origin-center`
				// resolves against the viewBox, so it silently moves the arc out of view
				// whenever the viewBox is not `0 0 2r 2r` (see the cropped `inset={false}` case).
				style={{ strokeDashoffset, transformOrigin: `${radius}px ${radius}px` }}
				r={normalizedRadius}
				cx={radius}
				cy={radius}
			/>
			{text != null && (
				<text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="22" fill="rgb(var(--text))">
					{text}
				</text>
			)}
		</svg>
	);
}
